import React, { ChangeEvent } from "react"

import {
	Box,
	FormControl,
	Grid,
	MenuItem,
	Typography,
} from "@mui/material"
import { ExpandMore } from "@mui/icons-material"

import Chart from "chart.js/auto"
import "chart.js"

import { theme } from "index"
import { customColors } from "customColors"
import { DashboardContractsPeriods } from "components/dashboard/dashboard-contracts/models"
import { SbSelect } from "components/commons/sb-select"
import { DashboardServices } from "services/dashboard.services"
import {
	ErrorResponse,
	RestResponse,
} from "@sinossi/mates-react-library"

export interface DashboardContractsData
{
	type: DashboardContractsPeriods,
	values: number[]
}

export interface DashboardContractsProps
{
	values: number[],
	labels: string[],
	onException: (response: RestResponse<any, ErrorResponse>) => void
}
export interface DashboardContractsState
{
	selectedFilter: string,
	values: number[],
	labels: string[]
}
export class DashboardContracts extends React.Component<DashboardContractsProps, DashboardContractsState>
{
	private palette = theme.palette
	private chartRef: React.RefObject<HTMLCanvasElement> = React.createRef()
	private myChart: Chart | null = null
	private myvalue: DashboardContractsPeriods | "" = DashboardContractsPeriods.MESE

	public componentDidMount()
	{
		this.createChart()
		window.addEventListener("resize", this.handleResize);
	}

	componentWillUnmount()
	{
		if(this.myChart) {
			this.myChart.destroy()
		}

		window.removeEventListener("resize", this.handleResize)
	}

	handleResize = () => {
		if(this.myChart) {
			this.myChart.resize()
		}
	}

	createChart = () => {
		const canvas = this.chartRef.current;

		if (canvas) {
			const ctx = canvas.getContext('2d');
			if (ctx) {
				if (this.myChart) {
					this.myChart.destroy();
				}

				this.myChart = new Chart(ctx, {
					type: 'line',
					data: {
						labels: this.props.labels,
						datasets: [{
							label: '',
							data: this.props.values,
							borderColor: this.palette.secondary.main,
							borderWidth: 2,
							fill: false,
							pointBackgroundColor: this.palette.secondary.main,
							pointBorderColor: this.palette.background.default,
							pointBorderWidth: 0.8
						}]
					},
					options: {
						maintainAspectRatio: false,
						plugins: {
							legend: {
								labels: {
									boxWidth: 0
								}
							}
						},
						scales: {
							x: {
								grid: {
									display: false,
								},
							},
							y: {
								suggestedMin: 0,
							},
						}
					}
				});
			}
		}
	}

	private convertStringToEnumValue = (stringValue: string): DashboardContractsPeriods => {
		for (const enumValue of Object.values(DashboardContractsPeriods)) {
			if (stringValue === enumValue) {
				return enumValue
			}
		}

		throw new Error(`The string value "${stringValue}" is not a valid enum value.`);
	};

	private handleChange = (event: React.ChangeEvent<{ value: string }>) => {
		this.myvalue = this.convertStringToEnumValue(event.target.value)
		switch(this.myvalue) {
			case DashboardContractsPeriods.SETTIMANA:
				DashboardServices.getDashboardContracts(
					DashboardContractsPeriods.SETTIMANA,
					this.getDashboardContractsCallback,
					this.props.onException
				)
				break;
			case DashboardContractsPeriods.MESE:
				DashboardServices.getDashboardContracts(
					DashboardContractsPeriods.MESE,
					this.getDashboardContractsCallback,
					this.props.onException
				)
				break;
			case DashboardContractsPeriods.TRIMESTRE:
				DashboardServices.getDashboardContracts(
					DashboardContractsPeriods.TRIMESTRE,
					this.getDashboardContractsCallback,
					this.props.onException
				)
				break;
			case DashboardContractsPeriods.ANNO:
				DashboardServices.getDashboardContracts(
					DashboardContractsPeriods.ANNO,
					this.getDashboardContractsCallback,
					this.props.onException
				)
		}

		this.setState({
			selectedFilter: event.target.value,
		});
	}

	private getDashboardContractsCallback = (response: RestResponse<DashboardContractsData, ErrorResponse>): void => {
		if (response.hasError()) {
			this.props.onException(response)
			return
		}

		this.setState(
			() => ({// @ts-ignore
				values: response.data.values,// @ts-ignore
				labels: this.calculateLabels(response.data.type)
			}),
			() => {
				if(this.myChart && this.state.values && this.state.labels) {
					this.myChart.data.labels = this.state.labels;
					this.myChart.data.datasets[0].data = this.state.values;
					this.myChart.update();
				}}
		)
	}

	private calculateLabels = (period: DashboardContractsPeriods): string[] => {
		const currentDate = new Date();
		const labels: string[] = [];
		const weekdays: string[] = [
			'Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'
		];
		const monthNames = [
			'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
			'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
		];

		switch (period) {
			case DashboardContractsPeriods.SETTIMANA:
				for (let i = 0; i < weekdays.length; i++) {
					const date = new Date(currentDate);
					date.setDate(currentDate.getDate() - (weekdays.length - 1 - i));
					labels.push(`${weekdays[i]} ${date.toLocaleDateString('it-IT', { month: 'short', day: '2-digit' })}`);
				}
				break;

			case DashboardContractsPeriods.MESE:
				const oneMonthAgo = new Date(currentDate);
				oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
				const formatter = new Intl.DateTimeFormat('it-IT', { month: 'short', day: '2-digit' });
				while (oneMonthAgo <= currentDate) {
					labels.push(formatter.format(oneMonthAgo));
					oneMonthAgo.setDate(oneMonthAgo.getDate() + 1);
				}
				break;

			case DashboardContractsPeriods.TRIMESTRE:
				for (let i = 2; i >= 0; i--) {
					const monthIndex = (currentDate.getMonth() - i + 12) % 12;
					labels.push(monthNames[monthIndex]);
				}
				break;

			case DashboardContractsPeriods.ANNO:
				for (let i = 11; i >= 0; i--) {
					const monthIndex = (currentDate.getMonth() - i + 12) % 12;
					labels.push(monthNames[monthIndex]);
				}
				break;

			default:
				throw new Error('Valore di periodo non valido');
		}

		return labels;
	}


	render()
	{
		return (
			<Box sx={{
				backgroundColor: this.palette.background.default,
				"height": "100%",
				display: "flex",
				flexDirection: "column",
			}}>
				<header>
					<Box sx={{ backgroundColor: customColors.storebookDarkBlue, paddingLeft: 3, paddingY: 2, color: 'white', textAlign: 'left' }}>
						<Typography fontSize={16}>
							Aperture
						</Typography>
					</Box>
				</header>

				<div>
					<Grid container>
						<Grid item xs={9} />
						<Grid item xs={3} style={{ justifyContent: "center", alignItems: 'center', display: 'flex' }}>
							<FormControl sx={{ m: 1, minWidth: 120, justifyContent: "flex-end", alignItems: "center" }} size="small">
								<SbSelect
									variant="outlined"
									size="small"
									IconComponent={ExpandMore}
									value={this.myvalue}
									onChange={(event) => {
										this.handleChange(event as ChangeEvent<{ value: string; }>)
									}}
								>
									{Object.values(DashboardContractsPeriods).map((filterValue, index) => (
										<MenuItem value={filterValue} key={index}>{filterValue}</MenuItem>
									))}
								</SbSelect>
							</FormControl>
						</Grid>
					</Grid>
				</div>

				<Box sx={{
					width: '90%',
					mx: "auto",
					mb: 5,
					flexGrow: 1,
				}}>
					<canvas ref={this.chartRef}></canvas>
				</Box>
			</Box>
		)
	}
}
